import { all, call, take, takeLatest, takeEvery } from "redux-saga/effects";

/* ------------- Types ------------- */
import * as actionTypes from "../actions/actionTypes";

/* ------------- Sagas ------------- */
import {
  updateUserSaga,
  updateUserSilentSaga,
  updateCvSaga,
  getUserSaga,
  storeCvStatusInSession,
  updateGeneralConsent,
  submitClientFormSilentSaga,
} from "./user";

import {
  onboardUpdateState,
  markDocumentRead,
  reflexAllenFormSubmit,
  getReflexAllenForm,
  postReflexAllenInductionProgramme,
} from "./onboard";

import { getAddressListSaga } from "./address";

import {
  requestTimesheetData,
  updateTimesheet,
  sendFeedback,
} from "./timesheet";

import {
  rightToWorkCheck,
  rightToWorkSuccess,
  rightToWorkVerificationQueueSize,
  rightToWorkVerificationQueueRegister,
  rightToWorkVerificationQueuePosition,
} from "./rightToWork";

import { uploadDocumentation, documentationSuccess } from "./documentation";

import applyForJobSaga from "./jobBoard";
import {
  handle404Error,
  handle500Error,
  handleOtherError,
} from "./errorHandler";
import { requestBookingData } from "./booking";
import { requestPayslipsData } from "./payslips";
import { requestLatestJobsData } from "./latestJobs";
import {
  postRoyalMailFormSaga,
  submitRoyalMailOnboardingSaga,
} from "./royalMail";
import {
  submitDnataOnboardingSaga,
  postDnataFormSaga,
  submitDnataTestSaga,
  submitDnataExtraFormSaga,
} from "./dnata";
import { postSwissportFormSaga } from "./swissport";
import {
  submitPecanAssessmentSaga,
  submitPecanFormsSaga,
  postPecanFormsSaga,
  markDocumentReadPecan,
} from "./pecan";
import { postDrivingFormSaga, submitDrivingOnboardingSaga } from "./driving";
import postNightWorkerFormSaga from "./nightWorker";
import postAuthorityForPaymentsFormSaga from "./authorityForPayments";
import updateDetailsStoreDate from "./updateDetailsPrompt";
import {
  loadNotifications,
  getLastReadDate,
  updateLastRead,
} from "./notifications";

import { putUserFeedbackSaga, putUserDismissalSaga } from "./userFeedback";

import markDocumentReadGB from "./greatBear";
import {
  markDocumentReadKingspan,
  postSOPKingspanSaga,
  submitKingspanAssessmentSaga,
} from "./kingspan";
import {
  markDocumentReadCarltonForest,
  postSSOWCarltonForest,
  postCarltonForestFormSaga,
  submitCarltonForestFormSaga,
} from "./carltonForest";
import {
  markDocumentReadPusterla,
  postPusterlaFormsSaga,
  submitPusterlaFormsSaga,
} from "./pusterla";

import { postGateFormSaga, submitGateOnboardingSaga } from "./gate";
import { postABMFormSaga, submitABMOnboardingSaga } from "./ABM";

import postNonverbalImmediacySaga from "./nonverbalImmediacyTest";
import postBig5PersonalitySaga from "./big5PersonalityTest";
import postExposureBasedMemorySaga from "./exposureBasedMemoryTest";
import postDiscTestAnswersSaga from "./discAssessmentTest";
import {
  postRegattaFormsSaga,
  submitRegattaFormsSaga,
  markDocumentReadRegatta,
  submitRegattaAssessmentSaga,
} from "./regatta";
import postNHSReferencesFormSaga from "./nhsReferencesForm";
import { submitGLAAFormsSaga } from "./GLAA";
import postUmbrellaDeclarationFormSaga from "./umbrellaDeclarationForm";
import { markDocumentReadCPI } from "./CPI";
import { fetchYotiIframeUrl } from "./yoti";
import {
  markDocumentReadCompleatFood,
  postCompleatFoodFormSaga,
} from "./compleatFood";
import {
  markDocumentReadMolecular,
  postMolecularFormsSaga,
  submitMolecularAssessmentSaga,
} from "./molecular";
import {
  markDocumentReadThanetCouncil,
  postRiskAssessmentThanetCouncilSaga,
  postThanetCouncilFormsSaga,
  submitThanetCouncilFormsSaga,
} from "./thanetCouncil";
import { markDocumentReadWoodland, postWoodlandFormsSaga } from "./woodland";
import { postHolidayRequestFormSaga } from "./holiday";
import {
  markDocumentReadGrowUpFarms,
  submitGrowUpFarmsFoodQuestionnaireSaga,
  submitGrowUpFarmsQuestionnaireSaga,
} from "./growUpFarms";
import {
  markDocumentReadDavinFoods,
  submitDavinFoodsQuizSaga,
  submitDavinFoodsFormSaga,
  postDavinFoodsFormSaga,
} from "./davinFoods";

import postIpnFormSaga from "./ipn";

function* takeOnlyOne(pattern, saga, ...args) {
  const action = yield take(pattern);

  yield call(saga, ...args.concat(action));
}

export default function* root() {
  yield all([
    takeLatest(actionTypes.ONBOARD_UPDATE_STATE, onboardUpdateState),
    takeLatest(actionTypes.UPDATE_USER_IN_PROFILE, updateUserSaga),
    takeLatest(actionTypes.UPDATE_USER_IN_PROFILE_SILENT, updateUserSilentSaga),
    takeLatest(actionTypes.UPDATE_GENERAL_CONSENT, updateGeneralConsent),
    takeLatest(actionTypes.TIMESHEETS_REQUEST_DATA, requestTimesheetData),
    takeLatest(actionTypes.TIMESHEET_IS_UPDATING, updateTimesheet),
    takeLatest(actionTypes.TIMESHEET_SEND_FEEDBACK, sendFeedback),
    takeLatest(actionTypes.BOOKING_REQUEST_DATA, requestBookingData),
    takeLatest(actionTypes.PAYSLIPS_REQUEST_DATA, requestPayslipsData),
    takeLatest(actionTypes.LATEST_JOBS_REQUEST_DATA, requestLatestJobsData),
    takeLatest(actionTypes.RTW_INITIATE_CHECK, rightToWorkCheck),
    takeLatest(actionTypes.RTW_CHECK_SUCCESS, rightToWorkSuccess),
    takeLatest(
      actionTypes.RTW_VERIFICATION_QUEUE_SIZE,
      rightToWorkVerificationQueueSize
    ),
    takeLatest(
      actionTypes.RTW_VERIFICATION_QUEUE_REGISTER,
      rightToWorkVerificationQueueRegister
    ),
    takeLatest(
      actionTypes.RTW_VERIFICATION_QUEUE_POSITION,
      rightToWorkVerificationQueuePosition
    ),
    takeLatest(actionTypes.DOCUMENTATION_INITIATE_UPLOAD, uploadDocumentation),
    takeLatest(actionTypes.MARK_DOCUMENT_READ, markDocumentRead),
    takeLatest(actionTypes.POST_REFLEX_ALLEN_FORM, reflexAllenFormSubmit),
    takeEvery(actionTypes.REQUEST_REFLEX_ALLEN_FORMS, getReflexAllenForm),
    takeLatest(
      actionTypes.POST_REFLEX_ALLEN_INDUCTION_PROGRAMME,
      postReflexAllenInductionProgramme
    ),
    takeLatest(actionTypes.MARK_DOCUMENT_READ_GB, markDocumentReadGB),
    takeLatest(actionTypes.MARK_DOCUMENT_READ_PECAN, markDocumentReadPecan),
    takeLatest(
      actionTypes.MARK_DOCUMENT_READ_KINGSPAN,
      markDocumentReadKingspan
    ),
    takeLatest(actionTypes.MARK_DOCUMENT_READ_REGATTA, markDocumentReadRegatta),
    takeLatest(
      actionTypes.MARK_DOCUMENT_READ_CARLTON_FOREST,
      markDocumentReadCarltonForest
    ),
    takeLatest(
      actionTypes.MARK_DOCUMENT_READ_THANET_COUNCIL,
      markDocumentReadThanetCouncil
    ),
    takeLatest(actionTypes.MARK_DOCUMENT_READ_CPI, markDocumentReadCPI),
    takeLatest(
      actionTypes.POST_CARLTON_FOREST_FORMS,
      postCarltonForestFormSaga
    ),
    takeLatest(
      actionTypes.SUBMIT_CARLTON_FOREST_FORMS,
      submitCarltonForestFormSaga
    ),
    takeLatest(actionTypes.POST_SSOW_CARLTON_FOREST, postSSOWCarltonForest),
    takeLatest(
      actionTypes.MARK_DOCUMENT_READ_PUSTERLA,
      markDocumentReadPusterla
    ),
    takeLatest(actionTypes.MARK_DOCUMENT_READ_PECAN, markDocumentReadPecan),
    takeLatest(actionTypes.DOCUMENTATION_SUCCESS, documentationSuccess),
    takeLatest(actionTypes.UPDATE_CV_SILENT, updateCvSaga),
    takeLatest(actionTypes.POST_ROYAL_MAIL_FORM, postRoyalMailFormSaga),
    takeLatest(
      actionTypes.SUBMIT_ROYAL_MAIL_ONBOARDING,
      submitRoyalMailOnboardingSaga
    ),
    takeLatest(actionTypes.POST_ABM_FORMS, postABMFormSaga),
    takeLatest(actionTypes.SUBMIT_ABM_FORMS, submitABMOnboardingSaga),
    takeLatest(actionTypes.POST_PUSTERLA_FORMS, postPusterlaFormsSaga),
    takeLatest(actionTypes.SUBMIT_PUSTERLA_FORMS, submitPusterlaFormsSaga),
    takeLatest(actionTypes.POST_GATE_FORM, postGateFormSaga),
    takeLatest(actionTypes.SUBMIT_GATE_FORM, submitGateOnboardingSaga),
    takeLatest(actionTypes.POST_DNATA_FORM, postDnataFormSaga),
    takeLatest(actionTypes.POST_SWISSPORT_FORM, postSwissportFormSaga),
    takeLatest(actionTypes.POST_SOP_KINGSPAN, postSOPKingspanSaga),
    takeLatest(
      actionTypes.SUBMIT_KINGSPAN_ASSESSMENT,
      submitKingspanAssessmentSaga
    ),
    takeLatest(actionTypes.SUBMIT_DNATA_FORM, submitDnataOnboardingSaga),
    takeLatest(actionTypes.SUBMIT_DNATA_TEST, submitDnataTestSaga),
    takeLatest(actionTypes.SUBMIT_DNATA_EXTRA_FORM, submitDnataExtraFormSaga),
    takeLatest(actionTypes.SUBMIT_PECAN_ASSESSMENT, submitPecanAssessmentSaga),
    takeLatest(actionTypes.SUBMIT_PECAN_FORMS, submitPecanFormsSaga),
    takeLatest(
      actionTypes.SUBMIT_THANET_COUNCIL_FORMS,
      submitThanetCouncilFormsSaga
    ),
    takeLatest(actionTypes.POST_PECAN_FORMS, postPecanFormsSaga),
    takeLatest(
      actionTypes.POST_THANET_COUNCIL_FORMS,
      postThanetCouncilFormsSaga
    ),
    takeLatest(actionTypes.POST_REGATTA_FORMS, postRegattaFormsSaga),
    takeLatest(actionTypes.SUBMIT_REGATTA_FORMS, submitRegattaFormsSaga),
    takeLatest(
      actionTypes.SUBMIT_REGATTA_ASSESSMENT,
      submitRegattaAssessmentSaga
    ),
    takeLatest(actionTypes.POST_NHS_REFERENCES_FORM, postNHSReferencesFormSaga),
    takeLatest(
      actionTypes.SUBMIT_UMBRELLA_DECLARATION_FORM,
      postUmbrellaDeclarationFormSaga
    ),
    takeLatest(actionTypes.POST_DRIVING_FORM, postDrivingFormSaga),
    takeLatest(actionTypes.POST_NIGHT_WORKER_FORM, postNightWorkerFormSaga),
    takeLatest(
      actionTypes.POST_AUTHORITY_FOR_PAYMENTS_FORM,
      postAuthorityForPaymentsFormSaga
    ),
    takeLatest(
      actionTypes.SUBMIT_DRIVING_ONBOARDING,
      submitDrivingOnboardingSaga
    ),
    takeLatest(actionTypes.UPDATE_DETAILS_STORE_DATE, updateDetailsStoreDate),
    takeLatest(actionTypes.GET_USER_IN_PROFILE, getUserSaga),
    takeLatest(actionTypes.GET_ADDRESS_LIST, getAddressListSaga),
    takeLatest(actionTypes.APPLY_FOR_JOB, applyForJobSaga),
    takeLatest(actionTypes.STORE_USER_DATA_IN_APP, storeCvStatusInSession),
    takeLatest(actionTypes.HTTP_404_ERROR, handle404Error),
    takeLatest(actionTypes.HTTP_500_ERROR, handle500Error),
    takeLatest(actionTypes.HTTP_OTHER_ERROR, handleOtherError),
    takeLatest(actionTypes.NOTIFICATIONS_LOAD_REQUEST, loadNotifications),
    takeLatest(actionTypes.NOTIFICATIONS_LAST_READ_REQUEST, getLastReadDate),
    takeLatest(actionTypes.POST_USER_FEEDBACK_DATA, putUserFeedbackSaga),
    takeLatest(actionTypes.POST_USER_FEEDBACK_DISMISSAL, putUserDismissalSaga),
    takeLatest(actionTypes.NOTIFICATIONS_READ_UPDATE_REQUEST, updateLastRead),
    takeLatest(actionTypes.POST_DISC_ASSESSMENT_TEST, postDiscTestAnswersSaga),
    takeLatest(
      actionTypes.POST_EXPOSURE_BASED_MEMORY_TEST,
      postExposureBasedMemorySaga
    ),
    takeLatest(actionTypes.SUBMIT_GLAA_FORMS, submitGLAAFormsSaga),
    takeLatest(
      actionTypes.POST_NONVERBAL_IMMEDIACY_TEST,
      postNonverbalImmediacySaga
    ),
    takeLatest(
      actionTypes.POST_BIG_5_PERSONALITY_TEST,
      postBig5PersonalitySaga
    ),
    takeLatest(
      actionTypes.POST_RISK_ASSESSMENT_THANET_COUNCIL,
      postRiskAssessmentThanetCouncilSaga
    ),
    takeOnlyOne(actionTypes.YOTI_FETCH_IFRAME, fetchYotiIframeUrl),
    takeLatest(
      actionTypes.MARK_DOCUMENT_READ_MOLECULAR,
      markDocumentReadMolecular
    ),
    takeLatest(actionTypes.POST_MOLECULAR_FORMS, postMolecularFormsSaga),
    takeLatest(actionTypes.POST_WOODLAND_FORMS, postWoodlandFormsSaga),
    takeLatest(
      actionTypes.SUBMIT_MOLECULAR_ASSESSMENT,
      submitMolecularAssessmentSaga
    ),
    takeLatest(
      actionTypes.MARK_DOCUMENT_READ_COMPLEAT_FOOD,
      markDocumentReadCompleatFood
    ),
    takeLatest(actionTypes.POST_COMPLEAT_FOOD_FORM, postCompleatFoodFormSaga),
    takeLatest(
      actionTypes.MARK_DOCUMENT_READ_WOODLAND,
      markDocumentReadWoodland
    ),
    takeLatest(
      actionTypes.POST_HOLIDAY_REQUEST_FORM,
      postHolidayRequestFormSaga
    ),
    takeLatest(
      actionTypes.MARK_DOCUMENT_READ_GROW_UP_FARMS,
      markDocumentReadGrowUpFarms
    ),
    takeLatest(
      actionTypes.SUBMIT_GROW_UP_FARMS_QUESTIONNAIRE,
      submitGrowUpFarmsQuestionnaireSaga
    ),
    takeLatest(
      actionTypes.SUBMIT_GROW_UP_FARMS_FOOD_QUESTIONNAIRE,
      submitGrowUpFarmsFoodQuestionnaireSaga
    ),
    takeLatest(
      actionTypes.MARK_DOCUMENT_READ_DAVIN_FOODS,
      markDocumentReadDavinFoods
    ),
    takeLatest(actionTypes.SUBMIT_DAVIN_FOODS_QUIZ, submitDavinFoodsQuizSaga),
    takeLatest(actionTypes.SUBMIT_DAVIN_FOODS_FORM, submitDavinFoodsFormSaga),
    takeLatest(actionTypes.POST_DAVIN_FOODS_FORM, postDavinFoodsFormSaga),
    takeLatest(actionTypes.POST_IPN_FORM, postIpnFormSaga),
    takeLatest(
      actionTypes.SUBMIT_CLIENT_FORM_SILENT,
      submitClientFormSilentSaga
    ),
  ]);
}
